import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { useTheme, useMediaQuery, Box, Typography, CircularProgress,
    FormControl, MenuItem, InputLabel, Select, Tooltip,
    ToggleButton,
} from '@mui/material';
import { Adjust } from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { setCrewBlockHours } from "state";
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchCrewBlockHours } from "state/api";
import { filterCrewMember } from "state/helpers";
import { hasPermission } from 'state/checkPermission';
import { countCrewBlockHours, countBHByTableau } from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import WorkloadStatsTable from 'components/WorkloadStatsTable';
import CommonTableau from './CommonTableau';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
// import ScatterPlotChart from 'components/chartNivo/ScatterPlotChart';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "charts",  label: "Charts" },
    { value: "stats",   label: "Stats" },
    { value: "tableau", label: "Tableau" },
];

const CrewBlockHoursChart = () => {    
    useAuthCheck();
    const dispatch = useDispatch();  
    const user = useSelector((state) => state.user); 
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const canViewAdjustedBH = hasPermission(user?.permissions, "CrewBlockHoursChart", "viewadjustedbh", false);
    
    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    
    const [isAdjusted, setIsAdjusted] = useState(false);
    const [minTotalHours, setMinTotalHours] = useState(0); 
    const [view, setView] = useState('bhByPostBase');
    const [displayType, setDisplayType] = useState('charts');
    const [data, setData] = useState({
        bhByPostBase: [],
        scatterBHByPostBase: [],
        bhByBase: [],
        scatterBHByBase: [],
        bhByPost: [],           
        scatterBHByPost: [],
        histogram: [],
    });

    const crewRanksData = useSelector((state) => state.crewRanks);
    const crewBlockHoursData = useSelector((state) => state.crewBlockHours);
        
    const [initialDateRange, setInitialDateRange] = useState(null);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    
    useEffect(() => {    
        const handleCrewBlockHours = async () => {
            if (!startDate || !endDate) return null; 
            
            try {
                setError(null)
                setLoading(true);

                const { data } = await fetchCrewBlockHours(startDate, endDate);
                if (data) {        
                    dispatch(setCrewBlockHours({ crewBlockHours: data }));
                }
            } catch (error) {
                setError(error)
                toast.error(error?.message || "Failed to fetch crew routes!");
            } finally {
                setLoading(false);
            }
        };
    
        if (!hasLoadedOnce) {
            if (crewBlockHoursData && crewBlockHoursData.length > 0) {
                // Có dữ liệu sẵn → không fetch
                setInitialDateRange({ startDate, endDate });
                setHasLoadedOnce(true);
                return;
            }

            // Không có dữ liệu → fetch lần đầu
            handleCrewBlockHours();
            setInitialDateRange({ startDate, endDate });
            setHasLoadedOnce(true);
            return;
        }

        // Sau lần đầu, nếu khoảng thời gian đổi → fetch lại
        if (initialDateRange && (startDate !== initialDateRange.startDate ||
            endDate !== initialDateRange.endDate)
        ) {
            handleCrewBlockHours();
            setInitialDateRange({ startDate, endDate });
        }  
    }, [
        dispatch,
        startDate, 
        endDate, 
        crewBlockHoursData, 
        hasLoadedOnce, 
        initialDateRange
    ]); 
    
    const filteredCrewBlockHours = useMemo(() => {
        if (!Array.isArray(crewBlockHoursData)) {
            return []; 
        }
        
        if (!filters) return crewBlockHoursData; 

        return crewBlockHoursData.filter(member => filterCrewMember(member, filters));
    }, [crewBlockHoursData, filters]);  
    
    useEffect(() => {        
        if (!filteredCrewBlockHours) return;
        
        const results = {            
            bhByPostBase: countCrewBlockHours(filteredCrewBlockHours, 'postbase', minTotalHours, 'barline', isAdjusted),
            scatterBHByPostBase: countCrewBlockHours(filteredCrewBlockHours, 'postbase', minTotalHours, 'scatterplot', isAdjusted),
            bhByPost: countCrewBlockHours(filteredCrewBlockHours, 'post', minTotalHours, 'barline', isAdjusted), 
            scatterBHByPost: countCrewBlockHours(filteredCrewBlockHours, 'post', minTotalHours, 'scatterplot', isAdjusted),
            bhByBase: countCrewBlockHours(filteredCrewBlockHours, 'base', minTotalHours, 'barline', isAdjusted),
            scatterBHByBase: countCrewBlockHours(filteredCrewBlockHours, 'base', minTotalHours, 'scatterplot', isAdjusted),
            histogram: countCrewBlockHours(filteredCrewBlockHours, 'all', minTotalHours, 'histogram', isAdjusted),
        };             
        
        setData(results);
    }, [filteredCrewBlockHours, minTotalHours, isAdjusted]); 
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };  

    const { baseOptions, postOptions, trainingQualOptions } = useMemo(() => {
        if (!crewBlockHoursData?.length) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
            };
        }
    
        const rankMap = new Map(
            (crewRanksData || []).map(rank => [rank.rankCode, rank])
        );

        const baseOptions = [...new Set(
            crewBlockHoursData.map(member => member.base).filter(Boolean)
        )].sort();

        const postOptions = [...new Set(
            crewBlockHoursData.map(member => member.post).filter(Boolean)
        )]
            .map(post => rankMap.get(post))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(rank => rank.rankCode);

        const trainingQualOptions = [...new Set(
            crewBlockHoursData
                .flatMap(member => member.trainingQuals || [])
                .filter(Boolean)
        )].sort();

        return { baseOptions, postOptions, trainingQualOptions };
    }, [crewBlockHoursData, crewRanksData]);

    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles, ...chart.style }}>
                {chart.component}
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Crew Block Hours ${period}`;
    
    const chartMap = {
        bhByPostBase: [
            { component: <SortBarMultiLinesChart data={data.bhByPostBase} title={defaultTitle} bottomLegend='Post & Base' leftLegend='Block Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            // { component: <ScatterPlotChart data={data.scatterBHByPostBase} digit={1} title={defaultTitle} bottomLegend='Sectors' leftLegend='Block Hours' />, style: {} },
        ],
        bhByPost: [
            { component: <SortBarMultiLinesChart data={data.bhByPost} title={defaultTitle} bottomLegend='Rank' leftLegend='Block Hours' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],        
        bhByBase: [
            { component: <SortBarMultiLinesChart data={data.bhByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Block Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
        histogram: [
            { component: <SortBarChart data={data.histogram} title={defaultTitle} bottomLegend='Block Hours' leftLegend='No. of Crew' isShowAvgLine={false} digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
    }; 
    
    const renderCharts = (charts, customStyles) => (
        charts.map((chart, index) => (
            <Box
                key={index}
                sx={{ ...styles, ...customStyles, ...chart.style }}
                onDoubleClick={() => handleChartOpen(chart)}
            >
                {chart.component}
            </Box>
        ))
    );


    const calcStats = (values) => {
        if (!values || values.length === 0) return null;

        // Filter value > 0
        const filtered = values.filter(v => v > 0);
        if (filtered.length === 0) return null;

        const n = filtered.length;
        const sum = filtered.reduce((a, b) => a + b, 0);
        const mean = sum / n;

        // Standard Deviation (Population)
        const variance = filtered.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
        const stdDev = Math.sqrt(variance);

        const max = Math.max(...filtered);
        const min = Math.min(...filtered);
        const gap = max - min;

        // Workload Equity Index (1 - stdDev/mean) → càng gần 1 càng cân bằng
        const equityIndex = mean > 0 ? 1 - stdDev / mean : 0;

        // Fairness Index (min / max) → càng gần 1 càng công bằng
        const fairnessIndex = max > 0 ? min / max : 0;

        return { mean, stdDev, gap, equityIndex, fairnessIndex };
    };
    
    const calcWorkLoadStats = useCallback((data, groupBy, minValue) => {
        const groups = data.reduce((acc, cur) => {
            let key;
            switch (groupBy) {
                case 'post': 
                    key = cur.post; 
                    break;
                case 'base': 
                    key = cur.base; 
                    break;
                case 'postbase': 
                    key = `${cur.post} ${cur.base}`; 
                    break;
                default: 
                    key = cur.crewCode; 
            }

            if (!acc[key]) {
                acc[key] = { values: [], 
                    members: [], 
                };
            }

            const val = cur.totalHours || 0;
            if (val > minValue) {
                acc[key].values.push(val);
            }
            acc[key].members.push(cur);

            return acc;
        }, {});

        const workloadStats = [];
        for (const [key, val] of Object.entries(groups)) {
            const stats = calcStats(val.values);
            if (stats) {
                workloadStats.push({
                    group: key,
                    mean: stats.mean,
                    stdDev: stats.stdDev,
                    gap: stats.gap,
                    equityIndex: stats.equityIndex,
                    fairnessIndex: stats.fairnessIndex
                });
            }
        }

        return workloadStats;
    }, []);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const bhField = isAdjusted ? 'adjustedTotalHours' : 'totalHours';
        const fieldMap = {
            bhByPostBase: ['post', 'base'],
            bhByPost:     ['post', null],
            bhByBase:     ['base', null],
        };
        const fields = fieldMap[view];
        if (!fields) return [];
        return countBHByTableau(filteredCrewBlockHours, fields[0], fields[1], bhField);
    }, [displayType, view, isAdjusted, filteredCrewBlockHours]);

    const statsData = useMemo(() => {
        if (!filteredCrewBlockHours || filteredCrewBlockHours.length === 0) return [];
        switch (view) {
            case "bhByPostBase":
            return calcWorkLoadStats(filteredCrewBlockHours, "postbase", minTotalHours);
            case "bhByPost":
            return calcWorkLoadStats(filteredCrewBlockHours, "post", minTotalHours);
            case "bhByBase":
            return calcWorkLoadStats(filteredCrewBlockHours, "base", minTotalHours);
            default:
            return [];
        }
    }, [view, filteredCrewBlockHours, calcWorkLoadStats, minTotalHours]);


    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header 
                    title={!isMobile ? "Crew Block Hours" : "Crew BH"} 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isMobile && (                           
                             <Tooltip title="Minimum Total Hours" arrow>
                                <span>
                                    <NumberControl
                                        min={0}
                                        max={99999}
                                        value={minTotalHours}
                                        onChange={(value) => { setMinTotalHours(value) }} 
                                        textFieldWidth={45}
                                    />
                                </span>
                            </Tooltip>
                        )}

                        {!isMobile && (
                            <FormControl 
                                size="small" 
                                sx={{ width: isMobile ? "6rem" : "8rem" }}
                            >
                                <InputLabel>Display</InputLabel>
                                <Select
                                    value={displayType}
                                    label="Display"
                                    onChange={(e) => setDisplayType(e.target.value)}
                                >
                                    {displayOptions.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        
                        <FormControl
                            size="small" 
                            sx={{ width: isMobile ? '6rem' : '10rem' }}
                        >
                            <InputLabel>View</InputLabel>
                            <Select
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}                               
                            >
                                <MenuItem value="bhByPostBase">by Rank & Base</MenuItem>
                                <MenuItem value="bhByPost">by Rank</MenuItem>
                                <MenuItem value="bhByBase">by Base</MenuItem>
                                {displayType === 'charts' && (
                                    <MenuItem value="histogram">Histogram</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        
                        {!isMobile && canViewAdjustedBH && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip title={isAdjusted ? "Cumulative BH" : "Full BH"} arrow>
                                    <ToggleButton        
                                        size="small"               
                                        value="isAdjusted"
                                        selected={isAdjusted}
                                        onChange={() => { setIsAdjusted(prev => !prev) }}
                                    >
                                        <Adjust />
                                    </ToggleButton>   
                                </Tooltip>
                                {/* <Tooltip title="Refresh" arrow>
                                    <span>
                                        <StyledButton 
                                            onClick={() => {
                                                
                                            }}
                                        >
                                            <Cached />
                                        </StyledButton>
                                    </span>
                                </Tooltip> */}
                            </Box>
                        )}
                        
                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                 
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    <Box 
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}                            
                    >
                        {displayType === "charts" ? (
                            view in chartMap &&
                                renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                        ) : displayType === "stats" ? (
                            <WorkloadStatsTable stats={statsData} />
                        ) : (
                            <Box sx={{ ...styles, minHeight: LAYOUT_HEIGHT }}>
                                <Box height="100%" width="100%">
                                    <CommonTableau data={tableauData} mode="blockHours" disableExpand />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
        
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&                
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    trainingQualOptions={trainingQualOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default CrewBlockHoursChart;
